import { forwardRef, useEffect, useRef, memo } from "react";
import { useGLTF } from "@react-three/drei";
import type { Object3D } from "three";
import type { BuildingProps } from "./types";
import { useSceneIndex } from "./useSceneIndex";
import { useBuildingHighlight } from "./useBuildingHighlight";

const Building = memo(
  forwardRef<Object3D, BuildingProps>(
    (
      {
        direction,
        selectedSegmentId,
        currentFloor,
        isFloorManuallySelected,
        onLoad,
      },
      ref,
    ) => {
      const { scene } = useGLTF("/models/1.11.glb");
      const onLoadCalled = useRef(false);

      // Build scene index (runs once on GLB load)
      const sceneIndex = useSceneIndex(scene);

      // Apply highlight state based on props
      useBuildingHighlight(
        sceneIndex,
        { direction, selectedSegmentId, currentFloor, isFloorManuallySelected },
        scene,
      );

      // Call onLoad callback once
      useEffect(() => {
        if (scene && onLoad && !onLoadCalled.current) {
          onLoadCalled.current = true;
          onLoad(scene);
        }
      }, [scene, onLoad]);

      if (!scene) return null;
      return <primitive ref={ref as any} object={scene} />;
    },
  ),
);

Building.displayName = "Building";

export default Building;

useGLTF.preload("/models/1.11.glb");
